/**
 * Plan 23/24 — Sync queue helpers.
 *
 * When a cafeteria mutation fires while offline, it's queued here so
 * Plan 24's replay manager can attempt it later. This file owns the
 * **storage** half:
 *
 *   - enqueue / list / count / remove rows from the live `syncQueue`
 *     (Plan 23 originals).
 *   - move-to-dead-letter / list / count / requeue / discard rows in
 *     `failedSyncQueue` (Plan 24 additions). The actual replay loop +
 *     conflict resolution live in `./replay-manager.ts`.
 *
 * Pure-ish: every Dexie reference is injected so tests stay
 * deterministic against an in-memory fake.
 */

import type { CafeteriaDb, FailedSyncQueueRow, SyncQueueOperation, SyncQueueRow } from './index'
import { getCafeteriaDb } from './index'

export interface EnqueueArgs {
  operation: SyncQueueOperation
  variables: Record<string, unknown>
  /** Optimistic UI patch the page applied locally — Plan 24 reads this on permanent failure to revert. */
  optimisticPatch?: unknown
  /** Override the generated id (tests) or `Date.now()`. */
  id?: string
  now?: number
}

/**
 * Generate a stable id when the caller didn't provide one. Falls back
 * to a `Math.random`-based id when `crypto.randomUUID` is unavailable
 * (older Safari running on a tablet).
 *
 * Pure — exported for tests.
 */
export function generateQueueId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }
  return `queue_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`
}

/**
 * Enqueue an outbound mutation. Returns the persisted row so the
 * caller can stash the id for an optimistic-success path.
 */
export async function enqueueMutation(args: EnqueueArgs, db: CafeteriaDb = getCafeteriaDb()): Promise<SyncQueueRow> {
  const now = args.now ?? Date.now()
  const row: SyncQueueRow = {
    id: args.id ?? generateQueueId(),
    operation: args.operation,
    variables: args.variables,
    optimisticPatch: args.optimisticPatch ?? null,
    createdAt: now,
    attempts: 0,
    lastAttemptAt: null,
    lastError: null,
  }
  await db.syncQueue.put(row)
  return row
}

/**
 * Count rows currently in the queue. Drives the ConnectivityChip's
 * "N pending" badge.
 *
 * Returns 0 on any Dexie error (defensive — the chip never throws).
 */
export async function countPendingMutations(db: CafeteriaDb = getCafeteriaDb()): Promise<number> {
  try {
    return await db.syncQueue.count()
  } catch {
    return 0
  }
}

/**
 * Count queued rows for ONE operation kind. Drives the POS
 * pending-charges pill (PROD-227 PR6), which counts `posSale` rows
 * only — serve/void rows belong to the ConnectivityChip's total.
 *
 * Returns 0 on any Dexie error (defensive — the pill never throws).
 */
export async function countPendingMutationsByOperation(
  operation: SyncQueueOperation,
  db: CafeteriaDb = getCafeteriaDb(),
): Promise<number> {
  try {
    return await db.syncQueue.where('operation').equals(operation).count()
  } catch {
    return 0
  }
}

/**
 * List queued rows for ONE operation kind (same `operation` index as
 * the count above). PROD-1122: the POS sale sheet folds the queued
 * `posSale` rows' `optimisticPatch.poolDraws` into the displayed
 * pool-remaining counts so a student rung twice during one outage
 * can't be shown pre-order units a queued sale already claimed.
 *
 * Returns [] on any Dexie error (defensive — display only).
 */
export async function listPendingMutationsByOperation(
  operation: SyncQueueOperation,
  limit = 200,
  db: CafeteriaDb = getCafeteriaDb(),
): Promise<SyncQueueRow[]> {
  try {
    const rows = await db.syncQueue.where('operation').equals(operation).toArray()
    return rows.slice(0, limit)
  } catch {
    return []
  }
}

/**
 * List rows oldest-first. Plan 24's replay loop reads this; the chip's
 * popover ("N pending serves") also previews the first few entries.
 */
export async function listPendingMutations(limit = 50, db: CafeteriaDb = getCafeteriaDb()): Promise<SyncQueueRow[]> {
  try {
    return await db.syncQueue.orderBy('createdAt').limit(limit).toArray()
  } catch {
    return []
  }
}

/**
 * Drop one row by id. Plan 24's replay manager calls this after a
 * successful server-side commit.
 */
export async function removeQueuedMutation(id: string, db: CafeteriaDb = getCafeteriaDb()): Promise<void> {
  try {
    await db.syncQueue.delete(id)
  } catch {
    // ignore — row may already be gone; that's idempotently fine.
  }
}

/**
 * Update attempt-tracking fields on a queued row. Plan 24's replay
 * loop calls this on a transient failure so the next pass can apply
 * exponential backoff.
 */
export interface UpdateAttemptArgs {
  id: string
  lastError: string | null
  now?: number
}

export async function recordReplayAttempt(args: UpdateAttemptArgs, db: CafeteriaDb = getCafeteriaDb()): Promise<void> {
  const now = args.now ?? Date.now()
  try {
    const row = await db.syncQueue.get(args.id)
    if (!row) {
      return
    }
    await db.syncQueue.put({
      ...row,
      attempts: row.attempts + 1,
      lastAttemptAt: now,
      lastError: args.lastError,
    })
  } catch {
    // ignore — the next replay pass will retry.
  }
}

// ---------------------------------------------------------------------------
// Plan 24 — failed-queue (dead-letter) helpers
// ---------------------------------------------------------------------------

/**
 * Build the dead-letter row from a live queue row. Pure — exported for
 * tests + reused by the replay manager so the row shape is consistent.
 */
export function toFailedRow(
  row: SyncQueueRow,
  args: { lastError: string; deadAt: number; attempts: number },
): FailedSyncQueueRow {
  return {
    id: row.id,
    operation: row.operation,
    variables: row.variables,
    optimisticPatch: row.optimisticPatch,
    createdAt: row.createdAt,
    attempts: args.attempts,
    lastAttemptAt: row.lastAttemptAt,
    lastError: args.lastError,
    deadAt: args.deadAt,
  }
}

/**
 * Atomically move a row from `syncQueue` → `failedSyncQueue`. Used by
 * the replay manager after exhausting all backoff attempts.
 */
export async function moveToFailedQueue(
  args: { id: string; lastError: string; attempts: number; now?: number },
  db: CafeteriaDb = getCafeteriaDb(),
): Promise<FailedSyncQueueRow | null> {
  const deadAt = args.now ?? Date.now()
  let written: FailedSyncQueueRow | null = null
  try {
    await db.transaction('rw', db.syncQueue, db.failedSyncQueue, async () => {
      const live = await db.syncQueue.get(args.id)
      if (!live) {
        return
      }
      const dead = toFailedRow(live, {
        lastError: args.lastError,
        deadAt,
        attempts: args.attempts,
      })
      await db.failedSyncQueue.put(dead)
      await db.syncQueue.delete(args.id)
      written = dead
    })
  } catch {
    // Either the row vanished mid-transaction or Dexie failed; the
    // next replay pass will retry the same row (idempotency-key dedup
    // on the server prevents double-commits).
  }
  return written
}

/**
 * Count rows in the dead-letter queue. Drives Plan 25's
 * `FailedReplayBanner` visibility + the chip's "X failed" badge.
 *
 * Returns 0 on any Dexie error (defensive — the banner never throws).
 */
export async function countFailedMutations(db: CafeteriaDb = getCafeteriaDb()): Promise<number> {
  try {
    return await db.failedSyncQueue.count()
  } catch {
    return 0
  }
}

/**
 * List dead-letter rows newest-first (by `deadAt`). The banner uses
 * the count; per-row UI (which serve / which error) is deferred.
 */
export async function listFailedMutations(
  limit = 50,
  db: CafeteriaDb = getCafeteriaDb(),
): Promise<FailedSyncQueueRow[]> {
  try {
    return await db.failedSyncQueue.orderBy('deadAt').reverse().limit(limit).toArray()
  } catch {
    return []
  }
}

/**
 * Move every dead-letter row back to the live `syncQueue` with
 * attempts reset to 0 so the replay manager can take another swing.
 * The replay manager picks them up on its next poll cycle without
 * needing an explicit kick.
 *
 * Used by Plan 25's "Retry" button.
 */
export async function requeueFailedMutations(db: CafeteriaDb = getCafeteriaDb()): Promise<number> {
  try {
    let count = 0
    await db.transaction('rw', db.syncQueue, db.failedSyncQueue, async () => {
      const rows = await db.failedSyncQueue.toArray()
      const requeued: SyncQueueRow[] = rows.map((r) => ({
        id: r.id,
        operation: r.operation,
        variables: r.variables,
        optimisticPatch: r.optimisticPatch,
        createdAt: r.createdAt,
        attempts: 0,
        lastAttemptAt: null,
        lastError: null,
      }))
      if (requeued.length > 0) {
        await db.syncQueue.bulkPut(requeued)
      }
      await db.failedSyncQueue.clear()
      count = requeued.length
    })
    return count
  } catch {
    return 0
  }
}

/**
 * Discard every dead-letter row without retrying. The cafeteria's
 * paper backup is the source of truth at that point.
 *
 * Used by Plan 25's "Discard" button.
 */
export async function discardFailedMutations(db: CafeteriaDb = getCafeteriaDb()): Promise<number> {
  try {
    const before = await db.failedSyncQueue.count()
    await db.failedSyncQueue.clear()
    return before
  } catch {
    return 0
  }
}
